import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  CheckSquare,
  MessageSquare,
  Timer,
  Eye,
  Code2,
  FileText,
  Terminal,
  Network,
  ShieldAlert,
  Projector,
  Youtube,
  ChevronLeft,
  ChevronRight,
  Mail,
  HelpCircle,
  CheckCircle,
  AlertTriangle,
  Search,
  Swords
} from 'lucide-react'
import type { LessonActivity } from '@/types'
import { PotatoPiratesActivity } from '@/components/potatoPirates/PotatoPiratesActivity'

interface ActivityPreviewProps {
  activity: LessonActivity | undefined
  loading: boolean
  lessonId?: string
}

const ACTIVITY_ICONS = {
  mcq: CheckSquare,
  coding: Code2,
  potato_pirates: Swords,
  pdf_slideshow: FileText,
  google_slides: Projector,
  command_shell: Terminal,
  phishing_quiz: ShieldAlert,
  packet_analysis: Network,
  cyber_hygiene_quiz: ShieldAlert,
  coding_quiz: Code2,
  python_repl: Code2,
  youtube_video: Youtube,
  breach_check: Search,
  password_check: ShieldAlert
}

export function ActivityPreview({ activity, loading, lessonId }: ActivityPreviewProps) {
  const [currentSlide, setCurrentSlide] = useState(1)
  if (!activity) {
    return (
      <div className="flex items-center justify-center h-full bg-[var(--color-surface-deep)]">
        <div className="text-center">
          <div className="text-[var(--color-muted)] mb-3">
            <Eye className="w-12 h-12 mx-auto" />
          </div>
          <p className="text-[var(--color-text)] mb-2 font-regular">No activity selected</p>
          <p className="text-sm text-[var(--color-text-muted)] font-light">Select an activity to see the preview</p>
        </div>
      </div>
    )
  }

  const IconComponent = ACTIVITY_ICONS[activity.activity_type as keyof typeof ACTIVITY_ICONS] || MessageSquare

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
  }

  const renderActivityContent = () => {
    if (!activity.activity_data) {
      return (
        <div className="text-center py-8">
          <p className="text-[var(--color-text-muted)] font-regular">No content configured</p>
        </div>
      )
    }

    switch (activity.activity_type) {
      case 'mcq':
        return renderMCQPreview()
      case 'coding':
        return renderCodingPreview()
      case 'potato_pirates':
        return renderPotatoPiratesPreview()
      case 'pdf_slideshow':
        return renderPdfSlideshowPreview()
      case 'google_slides':
        return renderGoogleSlidesPreview()
      case 'youtube_video':
        return renderYouTubeVideoPreview()
      case 'command_shell':
        return renderShellPreview()
      case 'phishing_quiz':
        return renderPhishingQuizPreview()
      case 'packet_analysis':
        return renderPacketAnalysisPreview()
      case 'cyber_hygiene_quiz':
        return renderCyberHygieneQuizPreview()
      case 'coding_quiz':
        return renderCodingQuizPreview()
      case 'python_repl':
        return renderPythonReplPreview()
      case 'breach_check':
        return renderBreachCheckPreview()
      case 'password_check':
        return renderPasswordCheckPreview()
      default:
        return renderGenericPreview()
    }
  }

  const renderBreachCheckPreview = () => (
    <div className="space-y-4">
      <div className="rounded-xl overflow-hidden border border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2 text-rose-500 font-semibold">
            <Search className="w-5 h-5" />
            Breach Check
          </div>
          <p className="text-sm text-[var(--color-text-muted)]">
            Students enter an email to check if it has appeared in known data breaches.
          </p>
          <div className="space-y-2">
            <input
              type="email"
              disabled
              placeholder="Enter an email to check..."
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg text-sm opacity-60"
            />
            <button disabled className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg opacity-50 text-sm font-medium">
              <Search className="w-4 h-4" /> Check Breaches
            </button>
          </div>
          <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-500" />
              <span className="text-sm text-rose-400 font-medium">Example: Found in 3 breaches</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderPasswordCheckPreview = () => (
    <div className="space-y-4">
      <div className="rounded-xl overflow-hidden border border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2 text-amber-500 font-semibold">
            <ShieldAlert className="w-5 h-5" />
            Password Check
          </div>
          <p className="text-sm text-[var(--color-text-muted)]">
            Students check if a password has appeared in data breaches. Password is hashed client-side.
          </p>
          <div className="space-y-2">
            <input
              type="password"
              disabled
              placeholder="Enter a password to check..."
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg text-sm opacity-60"
            />
            <button disabled className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg opacity-50 text-sm font-medium">
              <Search className="w-4 h-4" /> Check Password
            </button>
          </div>
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span className="text-sm text-amber-400 font-medium">Example: Seen 1.2M times in breaches</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderABCDOptions = (options: string[], correctIndexOrIndices: number | number[], mono?: boolean) => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const correctSet = new Set(Array.isArray(correctIndexOrIndices) ? correctIndexOrIndices : [correctIndexOrIndices])
    return (
      <div className="space-y-2">
        {options.map((opt: string, i: number) => {
          const isCorrect = correctSet.has(i)
          return (
            <div
              key={i}
              className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-all flex items-center gap-2 ${
                isCorrect
                  ? 'border-[var(--color-success)] bg-[var(--color-success-subtle)] text-[var(--color-success)]'
                  : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)]'
              }`}
            >
              <span className="text-[var(--color-text-muted)] font-mono text-xs mr-1">
                {letters[i]}.
              </span>
              <span className={`flex-1 ${mono ? 'font-mono' : ''}`}>
                {opt}
              </span>
              {isCorrect && <CheckCircle className="w-4 h-4 text-[var(--color-success)] ml-auto shrink-0" />}
            </div>
          )
        })}
      </div>
    )
  }

  const renderMCQPreview = () => {
    const data = activity.activity_data as any
    const correctIndices: number[] = Array.isArray(data.correct_answers)
      ? data.correct_answers
      : (typeof data.correct_answer === 'number' ? [data.correct_answer] : [])
    return (
      <div className="space-y-4">
        <div className="rounded-xl overflow-hidden border border-[var(--color-border)] bg-[var(--color-surface)]">
          <div className="p-5">
            <p className="text-sm font-semibold text-green-500 leading-relaxed whitespace-pre-wrap mb-4">
              {data.question || 'Question will appear here'}
            </p>
            {data.question_image_url && (
              <img
                src={data.question_image_url}
                alt="Question"
                className="max-h-48 rounded-lg object-contain border border-[var(--color-border)] mb-4"
              />
            )}
            {renderABCDOptions(data.options || [], correctIndices)}
            <button disabled className="w-full py-3 rounded-lg font-semibold text-sm mt-4 text-white opacity-50" style={{ background: 'var(--gradient-primary)' }}>
              Submit Answer
            </button>
          </div>
          {(data.explanation || data.explanation_image_url) && (
            <div className="bg-[var(--color-surface)] px-5 py-3 border-t border-[var(--color-border)]">
              <p className="text-xs font-semibold text-[var(--color-success)] mb-1">Explanation</p>
              {data.explanation && <p className="text-xs text-[var(--color-text)] leading-relaxed">{data.explanation}</p>}
              {data.explanation_image_url && <img src={data.explanation_image_url} alt="" className="mt-2 max-w-full max-h-48 rounded-lg object-contain" />}
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderCodingPreview = () => {
    const data = activity.activity_data as any
    const testCases: any[] = data.test_cases || []
    const hints: string[] = data.hints || []
    const categories: string[] = data.allowedCategories || ['Logic', 'Loops', 'Math', 'Text', 'Variables', 'Functions']

    const categoryColors: Record<string, string> = {
      Logic: '#5b80a5',
      Loops: '#5ba55b',
      Math: '#5b67a5',
      Text: '#a55b80',
      Variables: '#a55b5b',
      Functions: '#995ba5',
    }

    return (
      <div className="space-y-4">
        <div className="rounded-xl overflow-hidden border border-[var(--color-border)] bg-[var(--color-surface)]">
          <div className="px-5 py-3 bg-cyan-500/10 border-b border-cyan-500/20 flex items-center gap-2">
            <Code2 className="w-4 h-4 text-cyan-500" />
            <span className="text-sm font-semibold text-cyan-500">Block Coding Challenge</span>
          </div>

          <div className="p-6">
            <p className="text-sm font-semibold text-green-500 leading-relaxed whitespace-pre-wrap mb-5">
              {data.question || 'No question provided.'}
            </p>

            {/* Blockly editor mockup */}
            <div className="rounded-lg overflow-hidden border border-[var(--color-border)] shadow-sm mb-5">
              {/* Title bar */}
              <div className="bg-[var(--color-surface-deep)] px-3 py-2 flex items-center gap-2 border-b border-[var(--color-border)]">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <span className="ml-2 text-xs text-[var(--color-text-muted)] font-mono">Blockly Workspace</span>
              </div>
              {/* Editor body */}
              <div className="flex min-h-[180px]">
                {/* Toolbox sidebar */}
                <div className="w-24 bg-gray-800 border-r border-gray-700 py-2 shrink-0">
                  {categories.map((cat) => (
                    <div
                      key={cat}
                      className="px-2 py-1.5 text-[10px] text-gray-300 font-medium cursor-default hover:bg-gray-700 transition-colors flex items-center gap-1.5"
                    >
                      <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: categoryColors[cat] || '#888' }} />
                      {cat}
                    </div>
                  ))}
                </div>
                {/* Workspace canvas */}
                <div className="flex-1 bg-gray-100 relative overflow-hidden bg-[radial-gradient(circle,#d1d5db_1px,transparent_1px)] bg-[length:20px_20px]">
                  {/* Sample connected blocks */}
                  <div className="absolute top-6 left-6 flex flex-col">
                    <div className="flex items-center">
                      <div className="bg-[#a55b5b] text-white text-[10px] font-bold px-3 py-1.5 rounded-t-md rounded-l-md [clip-path:polygon(0_0,100%_0,100%_100%,12px_100%,8px_calc(100%-4px),4px_100%,0_100%)]">
                        set <span className="bg-white/20 px-1 rounded">x</span> to <span className="bg-white/30 px-1.5 rounded">0</span>
                      </div>
                    </div>
                    <div className="flex items-center -mt-px">
                      <div className="bg-[#5ba55b] text-white text-[10px] font-bold px-3 py-1.5 [clip-path:polygon(4px_0,8px_4px,12px_0,100%_0,100%_100%,12px_100%,8px_calc(100%-4px),4px_100%,0_100%)]">
                        repeat <span className="bg-white/30 px-1.5 rounded">10</span> times
                      </div>
                    </div>
                    <div className="flex items-center -mt-px ml-4">
                      <div className="bg-[#5b67a5] text-white text-[10px] font-bold px-3 py-1.5 [clip-path:polygon(4px_0,8px_4px,12px_0,100%_0,100%_100%,12px_100%,8px_calc(100%-4px),4px_100%,0_100%)]">
                        change <span className="bg-white/20 px-1 rounded">x</span> by <span className="bg-white/30 px-1.5 rounded">1</span>
                      </div>
                    </div>
                    <div className="flex items-center -mt-px">
                      <div className="bg-[#995ba5] text-white text-[10px] font-bold px-3 py-1.5 rounded-b-md [clip-path:polygon(4px_0,8px_4px,12px_0,100%_0,100%_100%,0_100%)]">
                        print <span className="bg-white/20 px-1 rounded">x</span>
                      </div>
                    </div>
                  </div>
                  {/* Floating hint */}
                  <div className="absolute bottom-3 right-3 text-[10px] text-gray-400 italic">Drag blocks to build your program</div>
                </div>
              </div>
            </div>

            {/* Run button mockup */}
            <div className="flex gap-2 mb-5">
              <button disabled className="px-5 py-2 rounded-lg text-sm font-medium opacity-50 flex items-center gap-1.5 bg-[var(--color-primary)] text-[var(--color-bg)]">
                <Code2 className="w-3.5 h-3.5" /> Run Code
              </button>
              <button disabled className="flex-1 py-2 rounded-lg text-sm font-semibold opacity-50 flex items-center justify-center gap-1.5 text-white" style={{ background: 'var(--gradient-primary)' }}>
                Submit
              </button>
            </div>

            {/* Test cases */}
            {testCases.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-[var(--color-text-muted)] mb-2">Test Cases</p>
                <div className="rounded-lg border border-[var(--color-border)] overflow-hidden">
                  {testCases.map((tc: any, i: number) => (
                    <div key={tc.id || i} className={`flex items-center gap-3 px-3 py-2 text-xs ${i > 0 ? 'border-t border-[var(--color-border)]' : ''}`}>
                      <span className="w-5 h-5 rounded bg-[var(--color-surface-deep)] text-[var(--color-text-muted)] flex items-center justify-center font-bold shrink-0">{i + 1}</span>
                      <span className="text-[var(--color-text)] truncate flex-1">{tc.description || `Test ${i + 1}`}</span>
                      <span className="font-mono text-[var(--color-text-muted)] shrink-0 bg-[var(--color-surface-deep)] px-2 py-0.5 rounded">
                        → {tc.expected_output}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hints */}
            {hints.length > 0 && (
              <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/30">
                <p className="text-xs font-semibold text-amber-500 mb-1.5">Hints ({hints.length})</p>
                <ul className="space-y-1">
                  {hints.map((hint: string, i: number) => (
                    <li key={i} className="text-xs text-amber-500 flex items-start gap-1.5">
                      <HelpCircle className="w-3 h-3 mt-0.5 shrink-0 text-amber-500/60" />
                      {hint}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderPotatoPiratesPreview = () => {
    const data = activity.activity_data as any
    return (
      <PotatoPiratesActivity
        activity={{ activity_data: data }}
        onSubmit={async () => {}}
        hideSubmit
      />
    )
  }

  const renderPdfSlideshowPreview = () => {
    const data = activity.activity_data as any
    const status: string = data.processing_status ?? 'pending'
    const totalSlides: number = data.total_slides ?? 0

    if (status === 'ready' && totalSlides > 0 && lessonId && activity.id) {
      const slide = Math.min(Math.max(currentSlide, 1), totalSlides)
      const slideUrl = `/api/v1/lessons/${lessonId}/activities/${activity.id}/slides?page=${slide}`

      return (
        <div className="space-y-4">
          <div className="bg-[var(--color-surface)] rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-orange-600" />
              <h3 className="ds-section-title">PDF Slideshow</h3>
              <span className="ml-auto text-sm text-[var(--color-text-muted)] font-regular">{totalSlides} slide{totalSlides === 1 ? '' : 's'}</span>
            </div>
            <div className="rounded-lg overflow-hidden border border-[var(--color-border)] bg-[var(--color-surface-deep)]">
              <img
                key={slideUrl}
                src={slideUrl}
                alt={`Slide ${slide} of ${totalSlides}`}
                className="w-full h-auto block min-h-[200px] object-contain bg-gray-100"
              />
            </div>
            <div className="flex items-center justify-between mt-3">
              <button
                onClick={() => setCurrentSlide(s => Math.max(1, s - 1))}
                disabled={slide <= 1}
                className="p-2 rounded-lg bg-[var(--color-surface-deep)] hover:bg-[var(--color-surface-hover)] disabled:opacity-30 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-[var(--color-text)]" />
              </button>
              <span className="text-sm text-[var(--color-text-muted)] font-regular">Slide {slide} of {totalSlides}</span>
              <button
                onClick={() => setCurrentSlide(s => Math.min(totalSlides, s + 1))}
                disabled={slide >= totalSlides}
                className="p-2 rounded-lg bg-[var(--color-surface-deep)] hover:bg-[var(--color-surface-hover)] disabled:opacity-30 transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-[var(--color-text)]" />
              </button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-orange-600" />
            <h3 className="ds-section-title">PDF Slideshow</h3>
          </div>
          <div className="border border-dashed border-[var(--color-border)] rounded-lg p-8 text-center bg-[var(--color-surface-deep)]">
            <FileText className="w-12 h-12 mx-auto mb-3 text-[var(--color-muted)]" />
            <p className="text-sm text-[var(--color-text-muted)] font-regular">
              {status === 'processing' ? 'PDF processing…' :
               status === 'failed' ? 'PDF processing failed. Re-upload in the editor.' :
               'Upload a PDF in the editor to get started'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  const renderGoogleSlidesPreview = () => {
    const data = activity.activity_data as any
    const embedUrl: string = data?.embed_url || ''

    if (embedUrl) {
      return (
        <div className="space-y-4">
          <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Projector className="w-5 h-5 text-yellow-600" />
              <h3 className="ds-section-title">Google Slides</h3>
            </div>
            <div
              className="relative w-full rounded-lg overflow-hidden border border-[var(--color-border)] pb-[56.25%]"
              onContextMenu={(e) => e.preventDefault()}
            >
              <iframe
                src={embedUrl}
                className="absolute inset-0 w-full h-full"
                sandbox="allow-scripts allow-same-origin"
                allowFullScreen
                loading="lazy"
                title="Google Slides Preview"
              />
            </div>
            <p className="text-xs text-[var(--color-muted)] mt-2">Go back and forth to navigate slides</p>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Projector className="w-5 h-5 text-yellow-600" />
            <h3 className="ds-section-title">Google Slides</h3>
          </div>
          <div className="border border-dashed border-[var(--color-border)] rounded-lg p-8 text-center bg-[var(--color-surface-deep)]">
            <Projector className="w-12 h-12 mx-auto mb-3 text-[var(--color-muted)]" />
            <p className="text-sm text-[var(--color-text-muted)] font-regular">
              Paste a Google Slides URL in the editor to get started
            </p>
          </div>
        </div>
      </div>
    )
  }

  const renderYouTubeVideoPreview = () => {
    const data = activity.activity_data as any
    const embedUrl: string = data?.embed_url || ''

    if (embedUrl) {
      return (
        <div className="space-y-4">
          <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Youtube className="w-5 h-5 text-red-600" />
              <h3 className="ds-section-title">YouTube Video</h3>
            </div>
            <div
              className="relative w-full rounded-lg overflow-hidden border border-[var(--color-border)] pb-[56.25%]"
              onContextMenu={(e) => e.preventDefault()}
            >
              <iframe
                src={embedUrl}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                title="YouTube Video Preview"
              />
            </div>
            <p className="text-xs text-[var(--color-muted)] mt-2">Native YouTube controls are hidden</p>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Youtube className="w-5 h-5 text-red-600" />
            <h3 className="ds-section-title">YouTube Video</h3>
          </div>
          <div className="border border-dashed border-[var(--color-border)] rounded-lg p-8 text-center bg-[var(--color-surface-deep)]">
            <Youtube className="w-12 h-12 mx-auto mb-3 text-[var(--color-muted)]" />
            <p className="text-sm text-[var(--color-text-muted)] font-regular">
              Paste a YouTube URL in the editor to get started
            </p>
          </div>
        </div>
      </div>
    )
  }

  const renderShellPreview = () => {
    const data = activity.activity_data as any
    const allowedCommands: string[] = data.allowed_commands || []
    return (
      <div className="space-y-4">
        {/* Problem card */}
        <div className="rounded-xl overflow-hidden border border-[var(--color-border)] bg-[var(--color-surface-deep)]">
          <div className="p-4">
            <p className="text-sm font-semibold text-green-500 leading-relaxed whitespace-pre-wrap mb-3">
              {data.question || 'Problem statement will appear here'}
            </p>
            <div>
              {data.target && (
                <span className="inline-block mt-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-500 font-mono text-xs rounded">
                  {data.target}
                </span>
              )}
              {data.hints && data.hints.length > 0 && (
                <p className="mt-2 text-xs text-[var(--color-text-muted)] italic">Hint: {data.hints[0]}</p>
              )}
            </div>
          </div>

          {/* Fake macOS terminal */}
          <div className="rounded-lg overflow-hidden border border-[var(--color-border)] shadow-sm">
            {/* Title bar */}
            <div className="bg-[var(--color-surface-deep)] px-3 py-2 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
              <span className="ml-2 text-xs text-[var(--color-text-muted)] font-mono">bash — 80×24</span>
            </div>
            {/* Terminal body */}
            <div className="bg-gray-900 text-green-400 font-mono text-xs p-4 min-h-[120px]">
              <div className="flex items-center gap-1">
                <span className="text-[var(--color-muted)]">$</span>
                <span className="animate-pulse">_</span>
              </div>
            </div>
          </div>

          {/* Answer input mockup */}
          <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-deep)] p-4 space-y-3">
            <input
              disabled
              placeholder="Type your answer here…"
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg text-sm bg-[var(--color-surface-hover)] text-[var(--color-muted)] font-mono"
            />
            <button
              disabled
              className="w-full py-3 rounded-lg text-sm font-semibold text-white opacity-50"
              style={{ background: 'var(--gradient-primary)' }}
            >
              Submit Answer
            </button>
          </div>
        </div>

        {/* Allowed commands chip list */}
        {allowedCommands.length > 0 && (
          <div className="bg-[var(--color-surface)] rounded-lg p-4 shadow-sm">
            <p className="text-xs font-semibold text-[var(--color-text)] mb-2">Allowed commands:</p>
            <div className="flex flex-wrap gap-1">
              {allowedCommands.map((cmd) => (
                <span key={cmd} className="px-2 py-0.5 bg-emerald-500/20 text-emerald-500 font-mono text-xs rounded">
                  {cmd}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderPhishingQuizPreview = () => {
    const data = activity.activity_data as any

    if (!data.subject && !data.question_id && !data.question) {
      return (
        <div className="space-y-4">
          <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <ShieldAlert className="w-5 h-5 text-red-600" />
              <h3 className="ds-section-title">Phishing Quiz</h3>
            </div>
            <p className="text-sm text-[var(--color-text-muted)] font-regular">
              Select a question from the bank or type your own to preview the email.
            </p>
          </div>
        </div>
      )
    }

    const initials = (data.fromName || 'U').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()
    const emailFont = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
    const fill = (s: string) => s.replace(/\{first\}/g, 'Alex').replace(/\{email\}/g, 'alex.student@school.edu')

    return (
      <div style={{ fontFamily: emailFont }} className="-mx-6 -mt-6">
        {/* Answer badge strip — outside the email sandbox */}
        <div className="flex items-center gap-2 flex-wrap px-5 py-3"
          style={{ background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)' }}>
          <span className={`ds-badge ${data.type === 'phishing' ? 'ds-badge-danger' : 'ds-badge-success'}`}>
            {data.type === 'phishing' ? 'Phishing' : 'Legitimate'}
          </span>
          {data.category && (
            <span className="ds-badge ds-badge-muted">{data.category}</span>
          )}
          {data.difficulty && (
            <span className={`ds-badge ${
              data.difficulty === 'easy' ? 'ds-badge-success' :
              data.difficulty === 'medium' ? 'ds-badge-warning' :
              'ds-badge-danger'
            }`}>{data.difficulty}</span>
          )}
        </div>

        {/* ── Email client sandbox ── */}
        <div style={{ background: '#ffffff', color: '#202124' }}>

          {/* Window chrome */}
          <div style={{ background: '#dee1e6', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 7, borderBottom: '1px solid #c4c7cc' }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ec6a5e' }} />
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#f4bf4f' }} />
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#61c554' }} />
            <span style={{ marginLeft: 12, fontSize: 12, color: '#5f6368', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Mail style={{ width: 13, height: 13 }} />
              Inbox
            </span>
          </div>

          {/* URL / chrome bar */}
          {data.chromeBar && (
            <div style={{ background: '#f1f3f4', padding: '6px 14px', borderBottom: '1px solid #dadce0', display: 'flex', alignItems: 'center' }}>
              <div style={{ flex: 1, background: '#fff', padding: '5px 12px', borderRadius: 20, fontSize: 12, color: '#5f6368', fontFamily: 'monospace', border: '1px solid #dadce0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                {fill(data.chromeBar)}
              </div>
            </div>
          )}

          {/* Email header */}
          <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e8eaed' }}>
            <h3 style={{ fontSize: 20, fontWeight: 400, color: '#202124', margin: '0 0 16px', lineHeight: 1.4 }}>
              {fill(data.subject || data.question || '')}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 14, fontWeight: 600, flexShrink: 0,
                backgroundColor: data.fromColor || '#1a73e8',
              }}>
                {initials}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#202124' }}>{data.fromName || 'Unknown Sender'}</span>
                  <span style={{ fontSize: 12, color: '#5f6368' }}>&lt;{data.fromEmail || 'unknown@example.com'}&gt;</span>
                </div>
                <p style={{ fontSize: 12, color: '#5f6368', margin: '2px 0 0' }}>to me</p>
              </div>
              {data.date && (
                <span style={{ marginLeft: 'auto', fontSize: 12, color: '#5f6368', flexShrink: 0 }}>{data.date}</span>
              )}
            </div>
          </div>

          {/* Email body */}
          <div className="phishing-email-body" style={{ padding: '20px 24px 28px', fontSize: 14, lineHeight: 1.7, color: '#3c4043' }}>
            {data.body ? (
              <div dangerouslySetInnerHTML={{ __html: fill(data.body) }} />
            ) : (
              <p style={{ color: '#9aa0a6', fontStyle: 'italic' }}>No email body configured</p>
            )}
          </div>
        </div>

        {/* Answer buttons — outside sandbox */}
        <div className="flex gap-3 px-5 py-4" style={{ background: 'var(--color-surface)', borderTop: '1px solid var(--color-border)' }}>
          <button disabled className="ds-btn flex-1 opacity-70 cursor-default"
            style={{ background: 'var(--color-danger-subtle)', color: 'var(--color-danger)', border: '1px solid var(--color-danger)' }}>
            <AlertTriangle className="w-4 h-4" /> Phishing
          </button>
          <button disabled className="ds-btn flex-1 opacity-70 cursor-default"
            style={{ background: 'var(--color-success-subtle)', color: 'var(--color-success)', border: '1px solid var(--color-success)' }}>
            <CheckCircle className="w-4 h-4" /> Legitimate
          </button>
        </div>

        {/* Hints */}
        {data.hints && data.hints.length > 0 && (
          <div className="mx-5 mb-4 p-3.5 rounded-lg"
            style={{ background: 'var(--color-warning-subtle)', border: '1px solid var(--color-warning)' }}>
            <p className="font-bold uppercase tracking-wide mb-1.5"
              style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-warning)', letterSpacing: '0.03em' }}>
              Hints ({data.hints.length})
            </p>
            <ul className="list-none m-0 p-0 flex flex-col gap-1">
              {data.hints.map((clue: string, i: number) => (
                <li key={i} className="flex items-start gap-1.5" style={{ fontSize: 'var(--fs-small)', color: 'var(--color-text)' }}>
                  <span className="shrink-0 mt-px" style={{ color: 'var(--color-warning)' }}>&#x2022;</span>
                  {clue}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }

  const renderPacketAnalysisPreview = () => {
    const data = activity.activity_data as any

    if (!data.question && !data.question_id) {
      return (
        <div className="space-y-4">
          <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Network className="w-5 h-5 text-violet-600" />
              <h3 className="ds-section-title">Packet Analysis</h3>
            </div>
            <p className="text-sm text-[var(--color-text-muted)] font-regular">
              Select a question from the bank to see the preview.
            </p>
          </div>
        </div>
      )
    }

    const options: string[] = data.options || []
    const correctIndex: number = data.correct_answer ?? -1
    const packets: any[] = data.packets || []

    const protoColor: Record<string, string> = {
      TCP: 'text-green-400 bg-green-900/30',
      UDP: 'text-blue-400 bg-blue-900/30',
      HTTP: 'text-yellow-400 bg-yellow-900/30',
      HTTPS: 'text-yellow-400 bg-yellow-900/30',
      DNS: 'text-blue-400 bg-blue-900/30',
      ARP: 'text-cyan-400 bg-cyan-900/30',
      ICMP: 'text-pink-400 bg-pink-900/30',
      TLS: 'text-purple-400 bg-purple-900/30',
      FTP: 'text-orange-400 bg-orange-900/30',
      SSH: 'text-indigo-400 bg-indigo-900/30',
    }

    return (
      <div className="space-y-4">
        {/* Question card */}
        <div className="rounded-xl overflow-hidden border border-[var(--color-border)] bg-[var(--color-surface)]">
          {/* Header with category + difficulty */}
          <div className="px-5 py-3 bg-violet-500/10 border-b border-violet-500/20 flex items-center gap-2 flex-wrap">
            <Network className="w-4 h-4 text-violet-500" />
            {data.category && (
              <span className="text-xs font-medium text-violet-500">{data.category}</span>
            )}
            {data.difficulty && (
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${
                data.difficulty === 'easy' ? 'bg-green-500/15 text-green-500' :
                data.difficulty === 'medium' ? 'bg-yellow-500/15 text-yellow-500' :
                'bg-red-500/15 text-red-500'
              }`}>{data.difficulty}</span>
            )}
          </div>

          <div className="p-5">
            {/* Packet table */}
            {packets.length > 0 && (
              <div className="rounded-lg overflow-hidden border border-[var(--color-border)] shadow-sm mb-5">
                <div className="bg-gray-900 text-gray-300 font-mono text-[10px]">
                  <div className="grid grid-cols-[2rem_3.5rem_5.5rem_5.5rem_2.5rem_2rem_1fr] gap-1 px-2 py-1.5 border-b border-gray-700 text-gray-500 font-semibold">
                    <span>No</span><span>Time</span><span>Source</span><span>Destination</span><span>Proto</span><span>Len</span><span>Info</span>
                  </div>
                  {packets.map((p: any) => (
                    <div key={p.no} className={`grid grid-cols-[2rem_3.5rem_5.5rem_5.5rem_2.5rem_2rem_1fr] gap-1 px-2 py-1 ${protoColor[p.protocol] || 'text-gray-300'}`}>
                      <span>{p.no}</span><span>{String(p.time).slice(0, 6)}</span><span className="truncate">{p.source}</span><span className="truncate">{p.destination}</span>
                      <span className="font-bold">{p.protocol}</span><span>{p.length}</span><span className="truncate">{p.info}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-sm font-semibold text-green-500 leading-relaxed whitespace-pre-wrap mb-5">
              {data.question}
            </p>

            {/* Options */}
            {renderABCDOptions(options, correctIndex)}
            <button disabled className="w-full py-3 rounded-lg font-semibold text-sm mt-4 text-white opacity-50" style={{ background: 'var(--gradient-primary)' }}>
              Submit Answer
            </button>
          </div>
        </div>

        {/* Explanation */}
        {(data.explanation || data.explanation_image_url) && (
          <div className="rounded-xl overflow-hidden border border-[var(--color-border)]">
            <div className="bg-[var(--color-surface)] px-5 py-3">
              <p className="text-xs font-semibold text-[var(--color-success)] mb-1">Explanation</p>
              {data.explanation && <p className="text-xs text-[var(--color-text)] leading-relaxed">{data.explanation}</p>}
              {data.explanation_image_url && <img src={data.explanation_image_url} alt="" className="mt-2 max-w-full max-h-48 rounded-lg object-contain" />}
            </div>
          </div>
        )}

        {/* Hints */}
        {data.hints && data.hints.length > 0 && (
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-5 py-3">
            <p className="text-xs font-semibold text-[var(--color-secondary)] mb-2">Hints ({data.hints.length})</p>
            <ul className="space-y-1">
              {data.hints.map((clue: string, i: number) => (
                <li key={i} className="text-xs text-[var(--color-text)] flex items-start gap-1.5">
                  <span className="text-[var(--color-secondary)] font-bold mt-0.5 shrink-0">&#x2022;</span>
                  {clue}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }

  const renderCyberHygieneQuizPreview = () => {
    const data = activity.activity_data as any

    if (!data.question && !data.question_id) {
      return (
        <div className="space-y-4">
          <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <ShieldAlert className="w-5 h-5 text-amber-500" />
              <h3 className="ds-section-title">Cyber Hygiene Quiz</h3>
            </div>
            <p className="text-sm text-[var(--color-text-muted)] font-regular">
              Select a question from the bank to see the preview.
            </p>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        <div className="rounded-xl overflow-hidden border border-[var(--color-border)] bg-[var(--color-surface)]">
          <div className="p-5">
            <p className="text-sm font-semibold text-green-500 leading-relaxed whitespace-pre-wrap mb-4">
              {data.question || 'Question will appear here'}
            </p>
            {renderABCDOptions(data.options || [], data.correct_answer ?? -1)}
            <button disabled className="w-full py-3 rounded-lg font-semibold text-sm mt-4 text-white opacity-50" style={{ background: 'var(--gradient-primary)' }}>
              Submit Answer
            </button>
          </div>
          {(data.explanation || data.explanation_image_url) && (
            <div className="bg-[var(--color-surface)] px-5 py-3 border-t border-[var(--color-border)]">
              <p className="text-xs font-semibold text-[var(--color-success)] mb-1">Explanation</p>
              {data.explanation && <p className="text-xs text-[var(--color-text)] leading-relaxed">{data.explanation}</p>}
              {data.explanation_image_url && <img src={data.explanation_image_url} alt="" className="mt-2 max-w-full max-h-48 rounded-lg object-contain" />}
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderCodingQuizPreview = () => {
    const data = activity.activity_data as any

    if (!data.question && !data.question_id) {
      return (
        <div className="space-y-4">
          <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Code2 className="w-5 h-5 text-indigo-500" />
              <h3 className="ds-section-title">Coding Quiz</h3>
            </div>
            <p className="text-sm text-[var(--color-text-muted)] font-regular">
              Select a question from the bank to see the preview.
            </p>
          </div>
        </div>
      )
    }

    const options: string[] = data.options || []
    const correctIndex: number = data.correct_answer ?? -1

    return (
      <div className="space-y-4">
        {/* Question card */}
        <div className="rounded-xl overflow-hidden border border-[var(--color-border)] bg-[var(--color-surface)]">
          {/* Header with category + difficulty */}
          <div className="px-5 py-3 bg-indigo-500/10 border-b border-indigo-500/20 flex items-center gap-2 flex-wrap">
            <Code2 className="w-4 h-4 text-indigo-500" />
            {data.category && (
              <span className="text-xs font-medium text-indigo-500">{data.category}</span>
            )}
            {data.difficulty && (
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${
                data.difficulty === 'easy' ? 'bg-green-500/15 text-green-500' :
                data.difficulty === 'medium' ? 'bg-yellow-500/15 text-yellow-500' :
                'bg-red-500/15 text-red-500'
              }`}>{data.difficulty}</span>
            )}
          </div>

          <div className="p-5">
            {data.title && (
              <p className="text-[11px] font-semibold text-[var(--color-muted)] uppercase tracking-wide mb-2">{data.title}</p>
            )}
            <p className="text-sm font-semibold text-green-500 leading-relaxed whitespace-pre-wrap mb-5">
              {data.question}
            </p>

            {/* Options */}
            {renderABCDOptions(options, correctIndex, true)}
            <button disabled className="w-full py-3 rounded-lg font-semibold text-sm mt-4 text-white opacity-50" style={{ background: 'var(--gradient-primary)' }}>
              Submit Answer
            </button>
          </div>
        </div>

        {/* Explanation */}
        {(data.explanation || data.explanation_image_url) && (
          <div className="rounded-xl overflow-hidden border border-[var(--color-border)]">
            <div className="bg-[var(--color-surface)] px-5 py-3">
              <p className="text-xs font-semibold text-[var(--color-success)] mb-1">Explanation</p>
              {data.explanation && <p className="text-xs text-[var(--color-text)] leading-relaxed">{data.explanation}</p>}
              {data.explanation_image_url && <img src={data.explanation_image_url} alt="" className="mt-2 max-w-full max-h-48 rounded-lg object-contain" />}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderPythonReplPreview = () => {
    const data = activity.activity_data as any

    if (!data.question && !data.question_id) {
      return (
        <div className="space-y-4">
          <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Code2 className="w-5 h-5 text-teal-500" />
              <h3 className="ds-section-title">Python Coding</h3>
            </div>
            <p className="text-sm text-[var(--color-text-muted)] font-regular">
              Select a question from the bank to see the preview.
            </p>
          </div>
        </div>
      )
    }

    const testCases: any[] = data.test_cases || []
    const starterCode: string = data.starter_code || '# Write your code here\n'
    const codeLines = starterCode.split('\n')

    return (
      <div className="space-y-4">
        <div className="rounded-xl overflow-hidden border border-[var(--color-border)] bg-[var(--color-surface)]">
          {/* Header */}
          <div className="px-5 py-3 bg-teal-500/10 border-b border-teal-500/20 flex items-center gap-2 flex-wrap">
            <Code2 className="w-4 h-4 text-teal-500" />
            <span className="text-xs font-medium text-teal-500">{data.category || 'Python'}</span>
            {data.difficulty && (
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${
                data.difficulty === 'easy' ? 'bg-green-500/15 text-green-500' :
                data.difficulty === 'medium' ? 'bg-yellow-500/15 text-yellow-500' :
                'bg-red-500/15 text-red-500'
              }`}>{data.difficulty}</span>
            )}
          </div>

          <div className="p-5">
            <p className="text-sm font-semibold text-green-500 leading-relaxed whitespace-pre-wrap mb-5">
              {data.question}
            </p>

            {/* Python code editor mockup */}
            <div className="rounded-lg overflow-hidden border border-[var(--color-border)] shadow-sm mb-5">
              {/* Title bar */}
              <div className="bg-[var(--color-surface-deep)] px-3 py-2 flex items-center gap-2 border-b border-[var(--color-border)]">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <span className="ml-2 text-xs text-[var(--color-text-muted)] font-mono">Python Editor</span>
              </div>
              {/* Editor body with line numbers */}
              <div className="flex bg-gray-900 min-h-[120px]">
                {/* Line numbers */}
                <div className="py-3 px-2 text-right select-none border-r border-gray-700 shrink-0">
                  {codeLines.map((_, i) => (
                    <div key={i} className="text-[11px] font-mono text-gray-600 leading-5">{i + 1}</div>
                  ))}
                </div>
                {/* Code content */}
                <pre className="py-3 px-4 font-mono text-xs leading-5 overflow-x-auto flex-1">
                  {codeLines.map((line, i) => (
                    <div key={i}>
                      {line.startsWith('#') || line.startsWith('//') ? (
                        <span className="text-gray-500">{line}</span>
                      ) : line.match(/^(def |class |import |from |return |if |elif |else|for |while |print|input)/) ? (
                        <>
                          <span className="text-purple-400">{line.match(/^(def |class |import |from |return |if |elif |else|for |while )/)?.[0]}</span>
                          <span className="text-green-400">{line.slice((line.match(/^(def |class |import |from |return |if |elif |else|for |while )/)?.[0] || '').length)}</span>
                        </>
                      ) : (
                        <span className="text-green-400">{line}</span>
                      )}
                    </div>
                  ))}
                </pre>
              </div>
            </div>

            {/* Action buttons mockup */}
            <div className="flex gap-2 mb-5">
              <button disabled className="px-5 py-2 rounded-lg text-sm font-semibold opacity-50 flex items-center gap-1.5 bg-[var(--color-primary)] text-[var(--color-bg)]">
                <Code2 className="w-3.5 h-3.5" /> Run Code
              </button>
              <button disabled className="flex-1 py-2 rounded-lg text-sm font-semibold opacity-50 flex items-center justify-center gap-1.5 text-white" style={{ background: 'var(--gradient-primary)' }}>
                Submit
              </button>
            </div>

            {/* Output panel mockup */}
            <div className="rounded-lg overflow-hidden border border-[var(--color-border)] mb-5">
              <div className="bg-[var(--color-surface-deep)] px-3 py-1.5 border-b border-[var(--color-border)]">
                <span className="text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">Output</span>
              </div>
              <div className="bg-gray-900 font-mono text-xs text-gray-500 p-3 min-h-[40px] italic">
                Run your code to see output here...
              </div>
            </div>

            {/* Test cases */}
            {testCases.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-[var(--color-text-muted)] mb-2">Test Cases</p>
                <div className="rounded-lg border border-[var(--color-border)] overflow-hidden">
                  {testCases.map((tc: any, i: number) => (
                    <div key={tc.id || i} className={`flex items-center gap-3 px-3 py-2 text-xs ${i > 0 ? 'border-t border-[var(--color-border)]' : ''}`}>
                      <span className="w-5 h-5 rounded bg-teal-500/20 text-teal-500 flex items-center justify-center font-bold shrink-0">{i + 1}</span>
                      <span className="text-[var(--color-text)] truncate flex-1">{tc.description || `Test ${i + 1}`}</span>
                      <span className="font-mono text-[var(--color-text-muted)] shrink-0 bg-[var(--color-surface-deep)] px-2 py-0.5 rounded">
                        → {tc.expected_output}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hints */}
            {data.hints && data.hints.length > 0 && (
              <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/30">
                <p className="text-xs font-semibold text-amber-500 mb-1">Hints</p>
                <p className="text-xs text-amber-500 flex items-start gap-1.5">
                  <HelpCircle className="w-3 h-3 mt-0.5 shrink-0 text-amber-500/60" />
                  {data.hints[0]}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderGenericPreview = () => {
    const data = activity.activity_data as any
    return (
      <div className="space-y-4">
        <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-sm">
          <h3 className="ds-section-title mb-4">
            {data.question || activity.title || 'Activity content will appear here'}
          </h3>
          {activity.instruction && (
            <div className="mt-4 p-3 bg-blue-500/10 rounded-lg">
              <p className="text-sm text-blue-400">{activity.instruction}</p>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Preview Header */}
      <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-pink-500/15 text-pink-500 rounded-lg">
              <IconComponent className="w-5 h-5" />
            </div>
            <div>
              <h2 className="ds-section-title">Activity Preview</h2>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {activity.time_limit_seconds && (
              <div className="flex items-center space-x-1 text-[var(--color-text-muted)]">
                <Timer className="w-4 h-4" />
                <span className="text-sm font-regular">{formatDuration(activity.time_limit_seconds)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-y-auto bg-[var(--color-surface-deep)]">
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-secondary)]" />
              <span className="ml-3 text-[var(--color-text-muted)] font-regular">Loading preview...</span>
            </div>
          ) : (
            <motion.div
              key={activity.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {renderActivityContent()}
            </motion.div>
          )}
        </div>
      </div>

    </div>
  )
}